import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Copy, Trash2, QrCode, Plus, Link2, CheckCircle, ExternalLink,
  Edit3, BarChart3, Globe, Smartphone, Monitor, X, Tag, Calendar,
  Lock, Eye, EyeOff, Loader2, Wand2, Brain, Sparkles, Route, Split,
  Laptop,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { linksAPI, getErrorMessage } from '../../services/api';

interface ShortLink {
  id: string;
  short_url: string;
  original_url: string;
  title: string | null;
  description: string | null;
  total_clicks: number;
  unique_clicks: number;
  is_active: boolean;
  qr_code_url: string | null;
  tags: string[];
  created_at: string;
  domain: string;
  expires_at: string | null;
  password: boolean;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  routing_rules: Array<{ condition: Record<string, string>; destination: string }>;
  ab_variants: Array<{ url: string; weight: number; clicks: number }>;
  group_id: string | null;
}

interface LinkAnalytics {
  total_clicks: number;
  unique_clicks: number;
  clicks_by_country: Record<string, number>;
  clicks_by_device: Record<string, number>;
  clicks_by_referrer: Record<string, number>;
  clicks_by_os: Record<string, number>;
  clicks_by_browser: Record<string, number>;
  clicks_by_date: Record<string, number>;
  recent_clicks: Array<{ timestamp: string; country?: string; device?: string; browser?: string; os?: string; referrer?: string }>;
}

interface LinkGroup {
  id: string;
  name: string;
  description: string | null;
  color: string;
  created_at: string;
}

interface AISummary {
  summary: string;
  insights: string[];
  recommendations: string[];
  generated_at: string;
}

export const ShortLinksPage: React.FC = () => {
  const { t } = useTranslation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedLink, setSelectedLink] = useState<ShortLink | null>(null);
  const [analyticsData, setAnalyticsData] = useState<LinkAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAnalyticsPanel, setShowAnalyticsPanel] = useState(false);
  const queryClient = useQueryClient();

  // Create form state
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [tags, setTags] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [password, setPassword] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editRoutingRules, setEditRoutingRules] = useState<Array<{ condition: Record<string, string>; destination: string }>>([]);
  const [editAbVariants, setEditAbVariants] = useState<Array<{ url: string; weight: number; clicks: number }>>([]);

  // Groups
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupColor, setGroupColor] = useState('#2383e2');

  // AI features
  const [aiLoading, setAiLoading] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState<AISummary | null>(null);

  const { data: groupsData } = useQuery({
    queryKey: ['link-groups'],
    queryFn: () => linksAPI.listGroups().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const { data: linksData, isLoading } = useQuery({
    queryKey: ['links', activeGroupId],
    queryFn: () => linksAPI.list({ group_id: activeGroupId || undefined }).then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const createLinkMutation = useMutation({
    mutationFn: () =>
      linksAPI.create({
        original_url: url,
        title: title || undefined,
        custom_code: customCode || undefined,
        utm_source: utmSource || undefined,
        utm_medium: utmMedium || undefined,
        utm_campaign: utmCampaign || undefined,
        tags: tags ? tags.split(',').map((tag) => tag.trim()).filter(Boolean) : [],
        expires_at: expiresAt || undefined,
        password: password || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
      setShowCreateForm(false);
      resetForm();
      toast.success(t('links_created', 'Link created!'));
    },
    onError: (err: unknown) => {
      toast.error(getErrorMessage(err, t('links_create_failed', 'Failed to create link')));
    },
  });

  const updateLinkMutation = useMutation({
    mutationFn: () =>
      linksAPI.update(selectedLink!.id, {
        title: editTitle || undefined,
        original_url: editUrl || undefined,
        tags: editTags ? editTags.split(',').map((tag) => tag.trim()).filter(Boolean) : [],
        is_active: editActive,
        routing_rules: editRoutingRules.length > 0 ? editRoutingRules : undefined,
        ab_variants: editAbVariants.length > 0 ? editAbVariants : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
      setShowEditModal(false);
      setSelectedLink(null);
      toast.success(t('links_updated', 'Link updated!'));
    },
    onError: (err: unknown) => {
      toast.error(getErrorMessage(err, t('links_update_failed', 'Failed to update link')));
    },
  });

  const deleteLinkMutation = useMutation({
    mutationFn: (linkId: string) => linksAPI.delete(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
      setSelectedLink(null);
      setShowAnalyticsPanel(false);
      toast.success(t('links_deleted', 'Link deleted'));
    },
    onError: () => toast.error(t('links_delete_failed', 'Failed to delete link')),
  });

  const resetForm = () => {
    setUrl(''); setTitle(''); setCustomCode('');
    setUtmSource(''); setUtmMedium(''); setUtmCampaign('');
    setTags(''); setExpiresAt(''); setPassword('');
    setShowAdvanced(false);
  };

  const handleCreateLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    await createLinkMutation.mutateAsync();
  };

  const handleCopy = (text: string, id?: string) => {
    navigator.clipboard.writeText(text);
    if (id) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
    toast.success(t('links_copied', 'Copied to clipboard'));
  };

  const openEdit = (link: ShortLink) => {
    setSelectedLink(link);
    setEditTitle(link.title || '');
    setEditUrl(link.original_url);
    setEditTags(link.tags.join(', '));
    setEditActive(link.is_active);
    setEditRoutingRules(link.routing_rules || []);
    setEditAbVariants(link.ab_variants || []);
    setAiSummary(null);
    setShowEditModal(true);
  };

  const openAnalytics = async (link: ShortLink) => {
    setSelectedLink(link);
    setShowAnalyticsPanel(true);
    setShowCreateForm(false);
    setAnalyticsLoading(true);
    setAiSummary(null);
    try {
      const res = await linksAPI.analytics(link.id);
      setAnalyticsData(res.data);
    } catch (err) {
      console.error('Analytics load error:', err);
      setAnalyticsData(null);
      toast.error(t('links_analytics_failed', 'Failed to load analytics'));
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const links: ShortLink[] = linksData?.links || [];

  const totalClicks = links.reduce((sum, l) => sum + l.total_clicks, 0);
  const totalUnique = links.reduce((sum, l) => sum + l.unique_clicks, 0);

  return (
    <div className="h-full flex flex-col bg-n-card overflow-hidden">
      <div className="px-6 pt-6 pb-4 border-b border-n-border">
        <h1 className="text-4xl font-bold text-n-text mb-1">🔗 {t('links_page_title', 'Short Links')}</h1>
        <p className="text-sm text-n-text-md">{t('links_page_subtitle', 'Create, manage, and track your shortened URLs.')}</p>
      </div>
      <div className="flex-1 overflow-auto px-6 pt-6 pb-4">
        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-semibold px-2 py-1 rounded-n-sm bg-n-hover text-n-text-md">
            {links.length} {t('links_items', 'links')}
          </span>
          <span className="text-[10px] font-semibold px-2 py-1 rounded-n-sm bg-n-blue-bg text-n-blue">
            {totalClicks.toLocaleString()} {t('links_stat_clicks', 'clicks')}
          </span>
          <span className="text-[10px] font-semibold px-2 py-1 rounded-n-sm bg-n-green-bg text-n-green">
            {totalUnique.toLocaleString()} {t('links_stat_unique', 'unique')}
          </span>
          <div className="flex-1" />
          <button
            onClick={() => { setShowCreateForm(true); setSelectedLink(null); setShowAnalyticsPanel(false); setAnalyticsData(null); setAiSummary(null); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2383e2] text-white rounded-n-sm font-medium text-xs transition-colors hover:opacity-90"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('links_create_button', 'Create Link')}
          </button>
        </div>

        {isLoading ? (
          <div className="bg-n-card rounded-n-sm border border-n-border p-4">
            <div className="text-center py-8">
              <div className="w-6 h-6 border-2 border-n-border rounded-n animate-spin mx-auto mb-2" style={{ borderTopColor: '#2383e2' }} />
              <p className="text-xs text-n-text-md">{t('links_loading', 'Loading links...')}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Group Filter Tabs */}
              {(groupsData?.groups || []).length > 0 && (
                <div className="flex items-center gap-1 mb-3 flex-wrap">
                  <button
                    onClick={() => setActiveGroupId(null)}
                    className={`px-2 py-1 text-[10px] font-semibold rounded-n-sm transition-colors ${
                      !activeGroupId ? 'bg-n-blue-bg text-n-blue' : 'text-n-text-md hover:bg-n-hover'
                    }`}
                  >
                    {t('links_all_links', 'All Links')}
                  </button>
                  {(groupsData?.groups || []).map((g: LinkGroup) => (
                    <button
                      key={g.id}
                      onClick={() => setActiveGroupId(g.id)}
                      className={`px-2 py-1 text-[10px] font-semibold rounded-n-sm transition-colors flex items-center gap-1 ${
                        activeGroupId === g.id ? 'bg-n-blue-bg text-n-blue' : 'text-n-text-md hover:bg-n-hover'
                      }`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: g.color }} />
                      {g.name}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowGroupForm(true)}
                    className="px-2 py-1 text-[10px] font-semibold text-n-text-lt hover:text-n-blue rounded-n-sm hover:bg-n-hover transition-colors"
                  >
                    + {t('links_new_group', 'New Group')}
                  </button>
                </div>
              )}

              <div className="bg-n-card rounded-n-sm border border-n-border">
                {links.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-10 h-10 rounded-n flex items-center justify-center mx-auto mb-3 bg-n-blue-bg">
                      <Link2 className="w-5 h-5 text-n-blue" />
                    </div>
                    <p className="text-sm font-bold text-n-text mb-1">{t('links_empty_title', 'No short links yet')}</p>
                    <p className="text-xs text-n-text-md mb-4">{t('links_empty_message', 'Create your first shortened URL to start tracking')}</p>
                    <button
                      onClick={() => { setShowCreateForm(true); setSelectedLink(null); setShowAnalyticsPanel(false); setAnalyticsData(null); setAiSummary(null); }}
                      className="px-4 py-1.5 bg-[#2383e2] text-white rounded-n-sm font-medium text-xs inline-flex items-center gap-1.5 transition-colors hover:opacity-90"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {t('links_create_first', 'Create First Link')}
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-n-border">
                    {links.map((link) => {
                      const shortCode = link.short_url.split('/').pop() || '';
                      return (
                        <div
                          key={link.id}
                          role="button"
                          tabIndex={0}
                          className={`p-3 transition-colors cursor-pointer hover:bg-n-hover ${
                            selectedLink?.id === link.id ? 'bg-n-hover' : ''
                          }`}
                          onClick={() => { setSelectedLink(link); setShowCreateForm(false); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedLink(link); setShowCreateForm(false); } }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              {link.title && (
                                <p className="text-xs font-semibold text-n-text mb-1">{link.title}</p>
                              )}

                              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                <span className="px-2 py-0.5 rounded-n-xs text-[10px] font-bold bg-n-blue-bg text-n-blue">
                                  {shortCode}
                                </span>
                                <span className="text-[10px] text-n-text-lt truncate">{link.short_url}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleCopy(link.short_url, link.id); }}
                                  className={`p-1 rounded transition ${copiedId === link.id ? '' : 'hover:bg-n-hover'}`}
                                  style={copiedId === link.id ? { backgroundColor: `#2383e215` } : undefined}
                                  title="Copy short URL"
                                >
                                  {copiedId === link.id ? (
                                    <CheckCircle className="w-3 h-3 text-n-blue" />
                                  ) : (
                                    <Copy className="w-3 h-3 text-n-text-lt" />
                                  )}
                                </button>
                                {!link.is_active && (
                                  <span className="px-1.5 py-0.5 bg-n-red-bg text-n-red text-[10px] font-semibold rounded-n">{t('links_inactive', 'Inactive')}</span>
                                )}
                                {link.password && (
                                  <span className="px-1.5 py-0.5 bg-n-amber-bg text-n-amber text-[10px] font-semibold rounded-n flex items-center gap-0.5">
                                    <Lock className="w-2 h-2" /> {t('links_protected', 'Protected')}
                                  </span>
                                )}
                                {link.routing_rules && link.routing_rules.length > 0 && (
                                  <span className="px-1.5 py-0.5 bg-n-blue-bg text-n-blue text-[10px] font-semibold rounded-n flex items-center gap-0.5">
                                    <Route className="w-2 h-2" /> {t('links_smart', 'Smart')}
                                  </span>
                                )}
                                {link.ab_variants && link.ab_variants.length > 0 && (
                                  <span className="px-1.5 py-0.5 bg-n-green-bg text-n-green text-[10px] font-semibold rounded-n flex items-center gap-0.5">
                                    <Split className="w-2 h-2" /> A/B
                                  </span>
                                )}
                              </div>

                              <p className="text-[11px] text-n-text-md mb-1 truncate">
                                <span className="text-gray-300 mr-1">→</span>
                                {link.original_url}
                              </p>

                              <div className="flex items-center gap-3 text-[10px] text-n-text-lt flex-wrap">
                                <span><span className="font-semibold text-n-text">{link.total_clicks.toLocaleString()}</span> {t('links_stat_clicks', 'clicks')}</span>
                                <span><span className="font-semibold text-n-text">{link.unique_clicks.toLocaleString()}</span> {t('links_stat_unique', 'unique')}</span>
                                <span>{new Date(link.created_at).toLocaleDateString()}</span>
                              </div>

                              {link.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {link.tags.map((tag) => (
                                    <span key={tag} className="px-1.5 py-0.5 bg-n-hover text-n-text-md text-[10px] rounded-n flex items-center gap-0.5">
                                      <Tag className="w-2 h-2" /> {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              <button
                                onClick={(e) => { e.stopPropagation(); openAnalytics(link); }}
                                className="p-1.5 hover:bg-n-blue-bg rounded-n-sm transition-colors"
                                title={t('links_analytics', 'Analytics')}
                              >
                                <BarChart3 className="w-3.5 h-3.5 text-n-blue" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); openEdit(link); }}
                                className="p-1.5 hover:bg-n-hover rounded-n-sm transition-colors"
                                title={t('links_edit', 'Edit')}
                              >
                                <Edit3 className="w-3.5 h-3.5 text-n-text-lt" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); if (confirm(t('links_confirm_delete', 'Delete this link?'))) deleteLinkMutation.mutate(link.id); }}
                                className="p-1.5 hover:bg-n-red-bg rounded-n-sm transition-colors"
                                title={t('links_delete', 'Delete')}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-n-red" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-3">
              {/* Analytics Panel */}
              {showAnalyticsPanel && selectedLink && !showCreateForm && (
                <div className="bg-n-card rounded-n-sm border border-n-border overflow-hidden">
                  <div className="px-3 py-2.5 border-b border-n-border flex items-center justify-between">
                    <h3 className="font-semibold text-n-text text-xs flex items-center gap-1.5">
                      <BarChart3 className="w-3.5 h-3.5 text-n-blue" />
                      {t('links_analytics_title', 'Link Analytics')}
                    </h3>
                    <button onClick={() => { setShowAnalyticsPanel(false); setAnalyticsData(null); }} className="p-1 hover:bg-n-hover rounded transition-colors">
                      <X className="w-3.5 h-3.5 text-n-text-lt" />
                    </button>
                  </div>

                  {analyticsLoading ? (
                    <div className="p-6 text-center">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-n-text-lt" />
                      <p className="text-[10px] text-n-text-lt">{t('links_analytics_loading', 'Loading analytics...')}</p>
                    </div>
                  ) : analyticsData ? (
                    <div className="p-3 space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-n-hover rounded-n-sm p-2.5 text-center">
                          <p className="text-sm font-bold text-n-text">{analyticsData.total_clicks}</p>
                          <p className="text-[10px] text-n-text-lt font-semibold">{t('links_total', 'Total')}</p>
                        </div>
                        <div className="bg-n-hover rounded-n-sm p-2.5 text-center">
                          <p className="text-sm font-bold text-n-text">{analyticsData.unique_clicks}</p>
                          <p className="text-[10px] text-n-text-lt font-semibold">{t('links_unique', 'Unique')}</p>
                        </div>
                      </div>

                      {Object.keys(analyticsData.clicks_by_device).length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-n-text-md mb-1.5 flex items-center gap-1">
                            <Smartphone className="w-2.5 h-2.5" /> {t('links_by_device', 'By Device')}
                          </p>
                          {Object.entries(analyticsData.clicks_by_device).sort(([, a], [, b]) => b - a).map(([device, count]) => (
                            <div key={device} className="flex items-center justify-between py-1">
                              <div className="flex items-center gap-1.5">
                                {device === 'mobile' ? <Smartphone className="w-2.5 h-2.5 text-n-text-lt" /> : <Monitor className="w-2.5 h-2.5 text-n-text-lt" />}
                                <span className="text-[10px] text-n-text capitalize">{device}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <div className="w-16 h-1 bg-n-hover rounded-n overflow-hidden">
                                  <div className="h-full rounded-n bg-[#2383e2]" style={{ width: `${(count / analyticsData.total_clicks) * 100}%` }} />
                                </div>
                                <span className="text-[10px] font-semibold text-n-text w-5 text-right">{count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {Object.keys(analyticsData.clicks_by_country).length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-n-text-md mb-1.5 flex items-center gap-1">
                            <Globe className="w-2.5 h-2.5" /> {t('links_by_country', 'By Country')}
                          </p>
                          {Object.entries(analyticsData.clicks_by_country).sort(([, a], [, b]) => b - a).slice(0, 5).map(([country, count]) => (
                            <div key={country} className="flex items-center justify-between py-1">
                              <span className="text-[10px] text-n-text">{country || 'Unknown'}</span>
                              <span className="text-[10px] font-semibold text-n-text">{count}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {Object.keys(analyticsData.clicks_by_os || {}).length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-n-text-md mb-1.5 flex items-center gap-1">
                            <Laptop className="w-2.5 h-2.5" /> {t('links_by_os', 'By OS')}
                          </p>
                          {Object.entries(analyticsData.clicks_by_os).sort(([, a], [, b]) => b - a).map(([os, count]) => (
                            <div key={os} className="flex items-center justify-between py-1">
                              <span className="text-[10px] text-n-text">{os}</span>
                              <div className="flex items-center gap-1.5">
                                <div className="w-16 h-1 bg-n-hover rounded-n overflow-hidden">
                                  <div className="h-full rounded-n bg-n-green" style={{ width: `${(count / analyticsData.total_clicks) * 100}%` }} />
                                </div>
                                <span className="text-[10px] font-semibold text-n-text w-5 text-right">{count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {Object.keys(analyticsData.clicks_by_referrer).length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-n-text-md mb-1.5">{t('links_top_referrers', 'Top Referrers')}</p>
                          {Object.entries(analyticsData.clicks_by_referrer).sort(([, a], [, b]) => b - a).slice(0, 5).map(([referrer, count]) => (
                            <div key={referrer} className="flex items-center justify-between py-1">
                              <span className="text-[10px] text-n-text truncate max-w-[140px]">{referrer || 'Direct'}</span>
                              <span className="text-[10px] font-semibold text-n-text">{count}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {Object.keys(analyticsData.clicks_by_date || {}).length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-n-text-md mb-1.5">{t('links_clicks_over_time', 'Clicks Over Time')}</p>
                          <div className="flex items-end gap-0.5 h-12">
                            {Object.entries(analyticsData.clicks_by_date).sort(([a], [b]) => a.localeCompare(b)).slice(-14).map(([date, count]) => {
                              const maxClicks = Math.max(...Object.values(analyticsData.clicks_by_date));
                              const height = maxClicks > 0 ? (count / maxClicks) * 100 : 0;
                              return (
                                <div key={date} className="flex-1 flex flex-col items-center gap-0.5" title={`${date}: ${count} clicks`}>
                                  <div className="w-full rounded-t bg-n-blue" style={{ height: `${Math.max(height, 4)}%` }} />
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex justify-between text-[8px] text-n-text-lt mt-0.5">
                            {(() => {
                              const dates = Object.keys(analyticsData.clicks_by_date).sort().slice(-14);
                              return dates.length > 1 ? <><span>{dates[0]}</span><span>{dates[dates.length - 1]}</span></> : <span>{dates[0]}</span>;
                            })()}
                          </div>
                        </div>
                      )}

                      {analyticsData.recent_clicks.length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-n-text-md mb-1.5">{t('links_recent_clicks', 'Recent Clicks')}</p>
                          <div className="max-h-32 overflow-y-auto space-y-0.5">
                            {analyticsData.recent_clicks.slice(-10).reverse().map((click, i) => (
                              <div key={i} className="flex items-center justify-between py-0.5 text-[10px] text-n-text-lt">
                                <span>{click.device || 'Unknown'} • {click.country || 'Unknown'}</span>
                                <span>{new Date(click.timestamp).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="border-t border-n-border pt-2">
                        <button
                          onClick={async () => {
                            setAiLoading('summary');
                            try {
                              const res = await linksAPI.aiSummary(selectedLink!.id);
                              setAiSummary(res.data);
                            } catch { toast.error(t('links_summary_failed', 'Failed to generate summary')); }
                            finally { setAiLoading(null); }
                          }}
                          disabled={aiLoading === 'summary'}
                          className="w-full px-2.5 py-1.5 text-xs font-semibold rounded-n-sm border border-n-border hover:bg-n-hover transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
                        >
                          {aiLoading === 'summary' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3 text-n-blue" />}
                          {t('links_ai_summary', 'AI Summary')}
                        </button>
                      </div>

                      {aiSummary && (
                        <div className="border-t border-n-border pt-2 space-y-2">
                          <p className="text-[10px] font-semibold text-n-text-md flex items-center gap-1">
                            <Brain className="w-2.5 h-2.5" /> {t('links_ai_insights', 'AI Insights')}
                          </p>
                          <p className="text-[10px] text-n-text">{aiSummary.summary}</p>
                          {aiSummary.insights.map((insight, i) => (
                            <p key={i} className="text-[10px] text-n-text-md pl-2 border-l-2 border-n-blue">{insight}</p>
                          ))}
                          {aiSummary.recommendations.length > 0 && (
                            <>
                              <p className="text-[10px] font-semibold text-n-text-md mt-1">{t('links_recommendations', 'Recommendations')}</p>
                              {aiSummary.recommendations.map((rec, i) => (
                                <p key={i} className="text-[10px] text-n-green pl-2 border-l-2 border-n-green">{rec}</p>
                              ))}
                            </>
                          )}
                        </div>
                      )}

                      {analyticsData.total_clicks === 0 && (
                        <p className="text-[10px] text-n-text-lt text-center py-3">{t('links_no_clicks', 'No clicks recorded yet')}</p>
                      )}
                    </div>
                  ) : (
                    <p className="p-3 text-[10px] text-n-text-lt text-center">{t('links_failed_analytics', 'Failed to load analytics')}</p>
                  )}
                </div>
              )}

              {/* QR Code / Link Detail */}
              {selectedLink && !showAnalyticsPanel && !showCreateForm && (
                <div className="bg-n-card rounded-n-sm border border-n-border overflow-hidden">
                  <div className="px-3 py-2.5 border-b border-n-border">
                    <h3 className="font-semibold text-n-text text-xs flex items-center gap-1.5">
                      <QrCode className="w-3.5 h-3.5 text-n-blue" />
                      {t('links_details_title', 'Link Details')}
                    </h3>
                  </div>

                  {selectedLink.qr_code_url && (
                    <div className="p-3 border-b border-n-border">
                      <div className="bg-n-hover p-3 rounded-n-sm border border-n-border mb-2">
                        <img src={selectedLink.qr_code_url} alt="QR Code" className="w-full" />
                      </div>
                      <button
                        onClick={() => {
                          const a = document.createElement('a');
                          a.href = selectedLink.qr_code_url!;
                          a.download = `qr-${selectedLink.short_url.split('/').pop()}.png`;
                          a.click();
                        }}
                        className="w-full px-3 py-1.5 text-white rounded-n-sm transition-colors text-xs font-semibold hover:opacity-90 bg-[#2383e2]"
                      >
                        {t('links_download_qr', 'Download QR Code')}
                      </button>
                    </div>
                  )}

                  <div className="p-3 space-y-2.5">
                    <div>
                      <p className="text-[10px] font-semibold text-n-text-lt mb-1">{t('links_short_url_label', 'Short URL')}</p>
                      <div className="flex items-center gap-1.5">
                        <input type="text" value={selectedLink.short_url} readOnly className="flex-1 px-2.5 py-1 bg-n-hover border border-n-border rounded-n-sm text-[10px] font-mono" />
                        <button onClick={() => handleCopy(selectedLink.short_url, `sidebar-${selectedLink.id}`)} className="p-1 hover:bg-n-hover rounded-n-sm transition">
                          <Copy className="w-3 h-3 text-n-text-lt" />
                        </button>
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] font-semibold text-n-text-lt mb-1">{t('links_original_url_label', 'Original URL')}</p>
                      <a href={selectedLink.original_url} target="_blank" rel="noopener noreferrer" className="text-[10px] break-all hover:underline flex items-center gap-1 text-n-blue">
                        {selectedLink.original_url}
                        <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                      </a>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <div className="bg-n-hover rounded-n-sm p-2 text-center">
                        <p className="text-sm font-bold text-n-text">{selectedLink.total_clicks}</p>
                        <p className="text-[10px] text-n-text-lt font-semibold">{t('links_total', 'Total')}</p>
                      </div>
                      <div className="bg-n-hover rounded-n-sm p-2 text-center">
                        <p className="text-sm font-bold text-n-text">{selectedLink.unique_clicks}</p>
                        <p className="text-[10px] text-n-text-lt font-semibold">{t('links_unique', 'Unique')}</p>
                      </div>
                    </div>

                    {selectedLink.tags.length > 0 && (
                      <div className="pt-1">
                        <p className="text-[10px] font-semibold text-n-text-lt mb-1">{t('links_tags_label', 'Tags')}</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedLink.tags.map((tag) => (
                            <span key={tag} className="px-1.5 py-0.5 bg-n-hover text-n-text-md text-[10px] rounded-n">{tag}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => openAnalytics(selectedLink)}
                        className="flex-1 px-2.5 py-1.5 text-xs font-semibold rounded-n-sm border border-n-border hover:bg-n-hover transition-colors flex items-center justify-center gap-1"
                      >
                        <BarChart3 className="w-3 h-3" /> {t('links_analytics_button', 'Analytics')}
                      </button>
                      <button
                        onClick={() => openEdit(selectedLink)}
                        className="flex-1 px-2.5 py-1.5 text-xs font-semibold rounded-n-sm border border-n-border hover:bg-n-hover transition-colors flex items-center justify-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" /> {t('links_edit_button', 'Edit')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Create Form */}
              {showCreateForm && (
                <div className="bg-n-card rounded-n-sm border border-n-border overflow-hidden">
                  <div className="px-3 py-2.5 border-b border-n-border flex items-center justify-between">
                    <h3 className="font-semibold text-n-text text-xs">{t('links_create_form_title', 'Create New Link')}</h3>
                    <button onClick={() => { setShowCreateForm(false); resetForm(); }} className="p-1 hover:bg-n-hover rounded transition-colors">
                      <X className="w-3.5 h-3.5 text-n-text-lt" />
                    </button>
                  </div>
                  <form onSubmit={handleCreateLink} className="p-3 space-y-3">
                    <div>
                      <label htmlFor="url" className="block text-xs font-semibold text-n-text mb-1">
                        {t('links_form_url_label', 'Original URL')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="url"
                        type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/long-page-url" required
                        className="w-full px-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label htmlFor="title" className="block text-xs font-semibold text-n-text">
                          {t('links_form_title_label', 'Title')} <span className="text-[10px] text-n-text-lt font-normal">{t('links_form_optional', '(optional)')}</span>
                        </label>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!url.trim()) { toast.error(t('links_enter_url_first', 'Enter a URL first')); return; }
                            setAiLoading('meta');
                            try {
                              const res = await linksAPI.aiGenerateMeta({ url });
                              setTitle(res.data.title);
                              toast.success(t('links_meta_generated', 'Title generated!'));
                            } catch { toast.error(t('links_meta_failed', 'Failed to generate title')); }
                            finally { setAiLoading(null); }
                          }}
                          disabled={aiLoading === 'meta'}
                          className="flex items-center gap-1 text-[10px] font-semibold text-n-blue hover:underline disabled:opacity-50"
                        >
                          {aiLoading === 'meta' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          {t('links_ai_generate_title', 'AI Generate')}
                        </button>
                      </div>
                      <input
                        id="title"
                        type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="My Campaign Link"
                        className="w-full px-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs"
                      />
                    </div>

                    <div>
                      <label htmlFor="custom-code" className="block text-xs font-semibold text-n-text mb-1">
                        {t('links_form_custom_code', 'Custom Code')} <span className="text-[10px] text-n-text-lt font-normal">{t('links_form_optional', '(optional)')}</span>
                      </label>
                      <input
                        id="custom-code"
                        type="text" value={customCode} onChange={(e) => setCustomCode(e.target.value)} placeholder="my-link"
                        className="w-full px-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs"
                      />
                    </div>

                    <div>
                      <label htmlFor="tags" className="block text-xs font-semibold text-n-text mb-1">
                        {t('links_form_tags', 'Tags')} <span className="text-[10px] text-n-text-lt font-normal">{t('links_form_comma_separated', '(comma-separated)')}</span>
                      </label>
                      <div className="relative">
                        <Tag className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-n-text-lt" />
                        <input
                          id="tags"
                          type="text" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="marketing, social, q1"
                          className="w-full pl-8 pr-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs"
                        />
                      </div>
                    </div>

                    <div className="pt-2 border-t border-n-border">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-n-text">
                          {t('links_form_utm_params', 'UTM Parameters')} <span className="text-[10px] text-n-text-lt font-normal">{t('links_form_optional', '(optional)')}</span>
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!url.trim()) { toast.error(t('links_enter_url_first', 'Enter a URL first')); return; }
                            setAiLoading('utm');
                            try {
                              const res = await linksAPI.aiUtmBuilder({ url, campaign_description: title || url });
                              setUtmSource(res.data.utm_source);
                              setUtmMedium(res.data.utm_medium);
                              setUtmCampaign(res.data.utm_campaign);
                              toast.success(t('links_utm_generated', 'UTM parameters generated!'));
                            } catch { toast.error(t('links_utm_failed', 'Failed to generate UTM')); }
                            finally { setAiLoading(null); }
                          }}
                          disabled={aiLoading === 'utm'}
                          className="flex items-center gap-1 text-[10px] font-semibold text-n-blue hover:underline disabled:opacity-50"
                        >
                          {aiLoading === 'utm' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                          {t('links_ai_generate_utm', 'AI Generate UTM')}
                        </button>
                      </div>
                      <div className="space-y-2">
                        <input type="text" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="Source (e.g., twitter)" className="w-full px-3 py-1.5 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs" />
                        <input type="text" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="Medium (e.g., social)" className="w-full px-3 py-1.5 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs" />
                        <input type="text" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="Campaign (e.g., spring-launch)" className="w-full px-3 py-1.5 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs" />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="text-[10px] font-semibold flex items-center gap-1 hover:underline text-n-blue"
                    >
                      {showAdvanced ? t('links_form_hide', 'Hide') : t('links_form_show', 'Show')} {t('links_form_advanced', 'Advanced Options')}
                    </button>

                    {showAdvanced && (
                      <div className="space-y-2 border-t border-n-border pt-2">
                        <div>
                          <label htmlFor="expires-at" className="block text-xs font-semibold text-n-text mb-1 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {t('links_form_expiration', 'Expiration Date')}
                          </label>
                          <input
                            id="expires-at"
                            type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
                            className="w-full px-3 py-1.5 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs"
                          />
                        </div>
                        <div>
                          <label htmlFor="password" className="block text-xs font-semibold text-n-text mb-1 flex items-center gap-1">
                            <Lock className="w-3 h-3" /> {t('links_form_password', 'Password Protection')}
                          </label>
                          <input
                            id="password"
                            type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Optional password"
                            className="w-full px-3 py-1.5 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs"
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 pt-2 border-t border-n-border">
                      <button
                        type="button" onClick={() => { setShowCreateForm(false); resetForm(); }}
                        className="flex-1 px-3 py-1.5 bg-n-hover text-n-text rounded-n-sm hover:bg-gray-200 transition-colors text-xs font-semibold"
                      >
                        {t('links_form_cancel', 'Cancel')}
                      </button>
                      <button
                        type="submit" disabled={createLinkMutation.isPending || !url.trim()}
                        className="flex-1 px-3 py-1.5 text-white rounded-n-sm disabled:opacity-50 transition-colors text-xs font-semibold hover:opacity-90 bg-[#2383e2]"
                      >
                        {createLinkMutation.isPending ? t('links_form_creating', 'Creating...') : t('links_form_submit', 'Create Link')}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Group Creation Modal */}
      {showGroupForm && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          role="button"
          tabIndex={-1}
          aria-label="Close group form"
          onClick={() => { setShowGroupForm(false); setGroupName(''); setGroupColor('#2383e2'); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { setShowGroupForm(false); setGroupName(''); setGroupColor('#2383e2'); } }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div className="bg-white rounded-xl w-[360px] border border-n-border" role="dialog" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 flex items-center justify-between border-b border-n-border">
              <h3 className="text-sm font-bold text-n-text">{t('links_new_group', 'New Group')}</h3>
              <button onClick={() => { setShowGroupForm(false); setGroupName(''); setGroupColor('#2383e2'); }} className="p-1 hover:bg-n-hover rounded transition-colors">
                <X className="w-4 h-4 text-n-text-lt" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-n-text mb-1">{t('links_form_group_name', 'Group Name')}</label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder={t('links_form_group_name_ph', 'e.g., Summer Campaign')}
                  className="w-full px-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-n-text mb-1">{t('links_form_group_color', 'Color')}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={groupColor}
                    onChange={(e) => setGroupColor(e.target.value)}
                    className="w-12 h-10 border border-n-border rounded-n-sm cursor-pointer"
                  />
                  <span className="text-[10px] text-n-text-lt font-mono">{groupColor}</span>
                </div>
              </div>

              <div className="flex gap-2 pt-2 border-t border-n-border">
                <button
                  onClick={() => { setShowGroupForm(false); setGroupName(''); setGroupColor('#2383e2'); }}
                  className="flex-1 px-3 py-1.5 bg-n-hover text-n-text rounded-n-sm hover:bg-gray-200 transition-colors text-xs font-semibold"
                >
                  {t('links_form_cancel', 'Cancel')}
                </button>
                <button
                  onClick={async () => {
                    if (!groupName.trim()) { toast.error(t('links_group_name_required', 'Group name is required')); return; }
                    try {
                      await linksAPI.createGroup({ name: groupName, color: groupColor });
                      queryClient.invalidateQueries({ queryKey: ['link-groups'] });
                      setShowGroupForm(false);
                      setGroupName('');
                      setGroupColor('#2383e2');
                      toast.success(t('links_group_created', 'Group created!'));
                    } catch (err: unknown) {
                      toast.error(getErrorMessage(err, t('links_group_create_failed', 'Failed to create group')));
                    }
                  }}
                  className="flex-1 px-3 py-1.5 text-white rounded-n-sm transition-colors text-xs font-semibold hover:opacity-90 bg-[#2383e2]"
                >
                  {t('links_form_create', 'Create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedLink && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          role="button"
          tabIndex={-1}
          aria-label="Close edit modal"
          onClick={() => setShowEditModal(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setShowEditModal(false); } }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div className="bg-white rounded-xl w-[420px] max-h-[80vh] overflow-auto border border-n-border" role="dialog" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 flex items-center justify-between border-b border-n-border">
              <h3 className="text-sm font-bold text-n-text">{t('links_edit_modal_title', 'Edit Link')}</h3>
              <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-n-hover rounded transition-colors">
                <X className="w-4 h-4 text-n-text-lt" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label htmlFor="edit-title" className="block text-xs font-semibold text-n-text mb-1">{t('links_form_title_label', 'Title')}</label>
                <input
                  id="edit-title"
                  type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="Link title"
                  className="w-full px-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs"
                />
              </div>

              <div>
                <label htmlFor="edit-url" className="block text-xs font-semibold text-n-text mb-1">{t('links_edit_destination_url', 'Destination URL')}</label>
                <input
                  id="edit-url"
                  type="url" value={editUrl} onChange={(e) => setEditUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs"
                />
              </div>

              <div>
                <label htmlFor="edit-tags" className="block text-xs font-semibold text-n-text mb-1">{t('links_edit_tags', 'Tags (comma-separated)')}</label>
                <input
                  id="edit-tags"
                  type="text" value={editTags} onChange={(e) => setEditTags(e.target.value)} placeholder="marketing, social"
                  className="w-full px-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:border-gray-300 text-xs"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />
                  <span className="text-xs text-n-text flex items-center gap-1">
                    {editActive ? <Eye className="w-3.5 h-3.5 text-green-500" /> : <EyeOff className="w-3.5 h-3.5 text-red-500" />}
                    {editActive ? t('links_edit_active', 'Active — link is live') : t('links_edit_inactive', 'Inactive — link is disabled')}
                  </span>
                </label>
              </div>

              <div className="border-t border-n-border pt-3">
                <p className="text-xs font-semibold text-n-text mb-2 flex items-center gap-1">
                  <Route className="w-3.5 h-3.5 text-n-blue" /> {t('links_smart_routing', 'Smart Routing')}
                </p>
                <p className="text-[10px] text-n-text-lt mb-2">{t('links_routing_desc', 'Route visitors to different URLs based on device, country, or OS.')}</p>
                {editRoutingRules.map((rule, i) => (
                  <div key={i} className="flex items-center gap-1.5 mb-1.5 text-[10px]">
                    <select value={Object.keys(rule.condition)[0] || ''} onChange={(e) => {
                      const newRules = [...editRoutingRules];
                      const val = Object.values(rule.condition)[0] || '';
                      newRules[i] = { ...rule, condition: { [e.target.value]: val } };
                      setEditRoutingRules(newRules);
                    }} className="px-1.5 py-1 border border-n-border rounded-n-xs text-[10px]">
                      <option value="device">Device</option>
                      <option value="country">Country</option>
                      <option value="os">OS</option>
                    </select>
                    <span className="text-n-text-lt">=</span>
                    <input
                      value={Object.values(rule.condition)[0] || ''}
                      onChange={(e) => {
                        const newRules = [...editRoutingRules];
                        const key = Object.keys(rule.condition)[0] || 'device';
                        newRules[i] = { ...rule, condition: { [key]: e.target.value } };
                        setEditRoutingRules(newRules);
                      }}
                      className="flex-1 px-1.5 py-1 border border-n-border rounded-n-xs text-[10px]"
                      placeholder="mobile"
                    />
                    <span className="text-n-text-lt">→</span>
                    <input
                      value={rule.destination}
                      onChange={(e) => {
                        const newRules = [...editRoutingRules];
                        newRules[i] = { ...rule, destination: e.target.value };
                        setEditRoutingRules(newRules);
                      }}
                      className="flex-1 px-1.5 py-1 border border-n-border rounded-n-xs text-[10px]"
                      placeholder="https://m.example.com"
                    />
                    <button onClick={() => {
                      setEditRoutingRules(editRoutingRules.filter((_, j) => j !== i));
                    }} className="p-0.5 hover:bg-n-red-bg rounded">
                      <X className="w-3 h-3 text-n-red" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setEditRoutingRules([...editRoutingRules, { condition: { device: '' }, destination: '' }])}
                  className="text-[10px] font-semibold text-n-blue hover:underline"
                >
                  + {t('links_add_rule', 'Add Rule')}
                </button>
              </div>

              <div className="border-t border-n-border pt-3">
                <p className="text-xs font-semibold text-n-text mb-2 flex items-center gap-1">
                  <Split className="w-3.5 h-3.5 text-n-amber" /> {t('links_ab_testing', 'A/B Testing')}
                </p>
                <p className="text-[10px] text-n-text-lt mb-2">{t('links_ab_desc', 'Split traffic between multiple destination URLs.')}</p>
                {editAbVariants.map((variant, i) => (
                  <div key={i} className="flex items-center gap-1.5 mb-1.5 text-[10px]">
                    <input
                      value={variant.url}
                      onChange={(e) => {
                        const newVariants = [...editAbVariants];
                        newVariants[i] = { ...variant, url: e.target.value };
                        setEditAbVariants(newVariants);
                      }}
                      className="flex-1 px-1.5 py-1 border border-n-border rounded-n-xs text-[10px]"
                      placeholder="https://example.com/variant-a"
                    />
                    <input
                      type="number" min={0} max={100} step={1}
                      value={Math.round(variant.weight * 100)}
                      onChange={(e) => {
                        const newVariants = [...editAbVariants];
                        newVariants[i] = { ...variant, weight: parseInt(e.target.value || '0') / 100 };
                        setEditAbVariants(newVariants);
                      }}
                      className="w-14 px-1.5 py-1 border border-n-border rounded-n-xs text-[10px] text-center"
                    />
                    <span className="text-n-text-lt">%</span>
                    {variant.clicks > 0 && (
                      <span className="text-[10px] text-n-text-md">{variant.clicks} clicks</span>
                    )}
                    <button onClick={() => {
                      setEditAbVariants(editAbVariants.filter((_, j) => j !== i));
                    }} className="p-0.5 hover:bg-n-red-bg rounded">
                      <X className="w-3 h-3 text-n-red" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setEditAbVariants([...editAbVariants, { url: '', weight: 0.5, clicks: 0 }])}
                  className="text-[10px] font-semibold text-n-blue hover:underline"
                >
                  + {t('links_add_variant', 'Add Variant')}
                </button>
              </div>

              <div className="border-t border-n-border pt-3 flex gap-2">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-3 py-1.5 bg-n-hover text-n-text rounded-n-sm hover:bg-gray-200 transition-colors text-xs font-semibold"
                >
                  {t('links_form_cancel', 'Cancel')}
                </button>
                <button
                  onClick={() => updateLinkMutation.mutate()}
                  disabled={updateLinkMutation.isPending}
                  className="flex-1 px-3 py-1.5 text-white rounded-n-sm disabled:opacity-50 transition-colors text-xs font-semibold hover:opacity-90 bg-[#2383e2]"
                >
                  {updateLinkMutation.isPending ? t('links_edit_saving', 'Saving...') : t('links_edit_save_changes', 'Save Changes')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShortLinksPage;
